import { apiRequest } from './client'
import type { RfqDto, RfqLineDto } from './types'

type RfqLineRaw = RfqLineDto & {
  product_type_id?: number
  sort_order?: number
  supplier_sku?: string | null
  product_name?: string | null
  line_status?: string
}

type RfqRaw = RfqDto & {
  organization_id?: number
  supplier_organization_id?: number
  created_at?: string
  updated_at?: string
  supplier_contact?: RfqDto['supplierContact']
  desired_delivery_date?: string | null
  desired_delivery_time_until?: string | null
  delivery_address?: string | null
  supplier_decision_status?: string | null
  rejection_reason_code?: string | null
  substitution_comment?: string | null
  buyer_substitution_comment?: string | null
  restaurantSubstitutionComment?: string | null
  restaurant_substitution_comment?: string | null
  customer_dispute_comment?: string | null
  lines?: RfqLineRaw[]
}

const normalizeLine = (raw: RfqLineRaw): RfqLineDto => ({
  id: raw.id,
  productTypeId: raw.productTypeId ?? raw.product_type_id ?? 0,
  quantity: typeof raw.quantity === 'string' ? Number(raw.quantity) : raw.quantity,
  unit: raw.unit,
  note: raw.note ?? null,
  sortOrder: raw.sortOrder ?? raw.sort_order ?? 0,
  supplierSku: raw.supplierSku ?? raw.supplier_sku ?? null,
  productName: raw.productName ?? raw.product_name ?? null,
  lineStatus: raw.lineStatus ?? raw.line_status ?? 'active',
})

export const normalizeRfq = (raw: RfqRaw): RfqDto => ({
  id: raw.id,
  organizationId: raw.organizationId ?? raw.organization_id ?? 0,
  supplierOrganizationId: raw.supplierOrganizationId ?? raw.supplier_organization_id ?? 0,
  title: raw.title,
  description: raw.description ?? null,
  status: raw.status,
  createdAt: raw.createdAt ?? raw.created_at ?? new Date(0).toISOString(),
  updatedAt: raw.updatedAt ?? raw.updated_at ?? new Date(0).toISOString(),
  supplierContact: raw.supplierContact ?? raw.supplier_contact ?? null,
  lines: (raw.lines ?? []).map(normalizeLine),
  desiredDeliveryDate: raw.desiredDeliveryDate ?? raw.desired_delivery_date ?? null,
  desiredDeliveryTimeUntil: raw.desiredDeliveryTimeUntil ?? raw.desired_delivery_time_until ?? null,
  deliveryAddress: raw.deliveryAddress ?? raw.delivery_address ?? null,
  supplierDecisionStatus: raw.supplierDecisionStatus ?? raw.supplier_decision_status ?? null,
  rejectionReasonCode: raw.rejectionReasonCode ?? raw.rejection_reason_code ?? null,
  substitutionComment: raw.substitutionComment ?? raw.substitution_comment ?? null,
  buyerSubstitutionComment:
    raw.buyerSubstitutionComment
    ?? raw.buyer_substitution_comment
    ?? raw.restaurantSubstitutionComment
    ?? raw.restaurant_substitution_comment
    ?? null,
  customerDisputeComment: raw.customerDisputeComment ?? raw.customer_dispute_comment ?? null,
})

export const listBuyerRfqs = async (organizationId: number) => {
  const items = await apiRequest<RfqRaw[]>(`/rfqs?organizationId=${organizationId}`)
  return items.map(normalizeRfq)
}

export const getBuyerRfq = async (rfqId: number) => {
  const rfq = await apiRequest<RfqRaw>(`/rfqs/${rfqId}`)
  return normalizeRfq(rfq)
}

/** Best-effort cancel: backend has no dedicated cancel; closes RFQ via status patch. */
export const closeBuyerRfq = async (rfqId: number) => {
  const rfq = await apiRequest<RfqRaw>(`/rfqs/${rfqId}/status`, {
    method: 'PATCH',
    body: { status: 'closed' },
  })
  return normalizeRfq(rfq)
}

export type RepeatRfqLine = {
  productTypeId: number
  quantity: number
  unit: string
  note?: string | null
}

export type RepeatRfqRequest = {
  organizationId: number
  supplierOrganizationId: number
  title: string
  description?: string | null
  desiredDeliveryDate?: string | null
  deliveryAddress?: string | null
  lines: RepeatRfqLine[]
}

export const createRfq = (request: RepeatRfqRequest) =>
  apiRequest<RfqRaw>('/rfqs', {
    method: 'POST',
    body: {
      organization_id: request.organizationId,
      supplier_organization_id: request.supplierOrganizationId,
      title: request.title,
      description: request.description?.trim() || undefined,
      desired_delivery_date: request.desiredDeliveryDate ?? undefined,
      delivery_address: request.deliveryAddress?.trim() || undefined,
      lines: request.lines.map((line) => ({
        product_type_id: line.productTypeId,
        quantity: line.quantity,
        unit: line.unit,
        note: line.note ?? undefined,
      })),
    },
  }).then(normalizeRfq)

export const confirmRfqCompletion = (
  rfqId: number,
  action: 'confirm' | 'dispute',
  comment?: string,
) =>
  apiRequest<RfqRaw>(`/rfqs/${rfqId}/confirm-completion`, {
    method: 'PATCH',
    body: {
      action,
      comment: comment?.trim() || undefined,
    },
  }).then(normalizeRfq)

export const respondRfqSubstitution = (
  rfqId: number,
  action: 'accept' | 'exclude_lines' | 'cancel',
  options?: { excludedLineIds?: number[]; comment?: string },
) =>
  apiRequest<RfqRaw>(`/rfqs/${rfqId}/respond-substitution`, {
    method: 'PATCH',
    body: {
      action,
      excluded_line_ids: options?.excludedLineIds,
      comment: options?.comment?.trim() || undefined,
    },
  }).then(normalizeRfq)
